import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Navbar from "../../../components/Navbar";
import PricingCard from "../../../components/Pricing/PricingCard";
import TrialModal from "../../../components/TrialModal";
import PaymentMethodModal from "../../../components/PaymentMethodModal";
import ProcessingModal from "../../../components/ProcessingModal";
import GuestRequests from "../../../requests/GuestRequests";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { useNavigate } from "react-router-dom";
import { usePermissionCheck } from "../../../hooks/permissionHooks/usePermissionCheck";
import { PERMISSIONS } from "../../../config/permissions";
import { Shield, AlertTriangle } from "lucide-react";

const Pricing = () => {
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessingOpen, setIsProcessingOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentDetails, setPaymentDetails] = useState({});
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Permission checks
  const { 
    canPerform, 
    isAuthenticated,
    isMainVendor 
  } = usePermissionCheck();

  // Check specific permissions
  const canPurchaseSubscription = canPerform(PERMISSIONS.MODULES.SUBSCRIPTION, PERMISSIONS.ACTIONS.PURCHASE_SUBSCRIPTION) || isMainVendor;

  // Debug logging for permission checks
  console.log('Pricing Permission Debug:', {
    isAuthenticated,
    isMainVendor,
    canPurchaseSubscription
  });

  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      if (!canPurchaseSubscription) return; // Don't fetch if no permission
      
      try {
        setIsLoading(true);
        const response = await GuestRequests.getSubscriptionPlans();
        if (
          response?.data?.status_code === "000" &&
          response?.data?.in_error === false
        ) {
          setSubscriptionPlans(response?.data?.data.slice(1, 4));
          setIsLoading(false);
        } else {
          toast.error(response?.data?.reason);
        }
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchSubscriptionPlans();
  }, [canPurchaseSubscription]);

  // Access denied screen for users without purchase permissions
  if (!isAuthenticated || !canPurchaseSubscription) {
    return (
      <div className="bg-white">
        <Navbar page="Pricing" />
        <div className="flex justify-center items-center min-h-[80vh] bg-gray-50">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-8 max-w-md text-center"
          >
            <div className="inline-block p-4 mb-6 bg-red-100 rounded-full">
              <Shield className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="mb-4 text-2xl font-bold text-gray-800">Access Restricted</h2>
            <p className="mb-6 leading-relaxed text-gray-600">
              You don't have permission to purchase subscriptions. Please contact your administrator to request access to the Subscription Purchase feature.
            </p>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-2 items-center mb-2">
                <AlertTriangle className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Required Permission:</span>
              </div>
              <p className="text-sm text-blue-700">Purchase Subscription in Subscription Management</p>
            </div>
            <div className="flex gap-3 mt-6">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/subscription')}
                className="px-4 py-2 font-medium text-white bg-gray-500 rounded-lg transition-colors hover:bg-gray-600"
              >
                View Subscription
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-[#FF6C6C] text-white rounded-lg hover:bg-[#FF6C6C]/90 transition-colors font-medium"
              >
                Return to Dashboard
              </motion.button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const handleContinue = () => {
    setIsModalOpen(false);
  };

  const getProcessingSteps = () => {
    if (paymentMethod === "card") {
      return [
        "Encrypting card details",
        "Verifying card information",
        "Processing payment",
        "Finalizing transaction",
      ];
    }
    return [
      "Initiating mobile money request",
      "Sending payment prompt",
      "Awaiting confirmation",
      "Verifying payment",
    ];
  };

  const handleModalOpen = (setModalOpen, details = {}) => {
    setModalOpen(true);
    setPaymentDetails(details);
  };

  const onComplete = () => {
    setIsProcessingOpen(false);
    setIsPaymentModalOpen(false);
  };

  const handleContactSales = () => {
    navigate('/contact-sales');
  };

  if(isLoading){
    return <Loader/>
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <Navbar page="Pricing" />
      
      {/* Enhanced Header Section */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#fff1f1] via-[#fff5f5] to-transparent">
        <div className="absolute inset-0">
          <div className="absolute -left-4 top-0 w-72 h-72 bg-[#FF6C6C] rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob"></div>
          <div className="absolute -right-4 top-0 w-72 h-72 bg-[#FF8F8F] rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-[#FFB4B4] rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-blob animation-delay-4000"></div>
        </div>
        
        <div className="relative px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col justify-center items-center"
            >
              <span className="px-4 py-1.5 mb-4 text-sm font-semibold text-[#FF6C6C] bg-[#FF6C6C]/10 rounded-full">
                Pricing Plans
              </span>
              <h1 className="mb-4 text-4xl font-bold text-gray-900 md:text-5xl lg:text-6xl">
                Choose Your{' '}
                <span className="bg-gradient-to-r from-[#FF6C6C] to-[#FF9494] bg-clip-text text-transparent">
                  Growth Plan
                </span>
              </h1>
              <p className="mx-auto max-w-2xl text-lg text-gray-600 md:text-xl">
                Join thousands of successful businesses and unlock powerful features to scale your business
              </p>

              {/* Feature Highlights */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="grid grid-cols-2 gap-4 mx-auto mt-12 max-w-4xl md:grid-cols-4"
              >
                {[
                  {
                    icon: "💳",
                    text: "Flexible Payments",
                    desc: "Flexible payment"
                  },
                  {
                    icon: "🔒",
                    text: "Enterprise Security",
                    desc: "Reliable encryption"
                  },
                  {
                    icon: "🚀",
                    text: "Instant Setup",
                    desc: "Quick setup"
                  },
                  {
                    icon: "📊",
                    text: "Instant Transfer",
                    desc: "Real-time transaction"
                  }
                ].map((feature, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="p-6 text-center rounded-2xl border border-gray-100 shadow-sm backdrop-blur-sm transition-all duration-300 bg-white/60 hover:shadow-md"
                  >
                    <div className="mb-3 text-3xl">{feature.icon}</div>
                    <div className="mb-1 font-semibold text-gray-900">{feature.text}</div>
                    <div className="text-sm text-gray-600">{feature.desc}</div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      <div className="px-4 mx-auto max-w-6xl mx sm:px-6 lg:px-8">
        {/* Pricing Cards Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex relative z-10 flex-col gap-4 justify-center items-center p-4 mb-16 md:flex-row"
        >
          {subscriptionPlans.map((plan, index) => (
            <PricingCard
              key={index}
              {...plan}
              onSelect={() => handleModalOpen(setIsPaymentModalOpen, plan)}
              index={index}
            />
          ))}
        </motion.div>

        {/* Custom Enterprise Plan */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="relative mx-auto mb-24 max-w-4xl"
        >
          <div className="absolute inset-0 transform -skew-y-2">
            <div className="absolute inset-0 bg-gradient-to-r from-[#FF6C6C]/10 to-[#FF9494]/10 rounded-xl"></div>
          </div>
          <div className="relative p-8 bg-white/80 backdrop-blur-sm rounded-xl border border-[#FF6C6C]/20 shadow-xl">
            <div className="flex flex-col gap-8 justify-between items-center md:flex-row">
              <div className="flex-1">
                <div className="inline-block px-4 py-1.5 mb-4 text-sm font-semibold text-[#FF6C6C] bg-[#FF6C6C]/10 rounded-full">
                  Platinum Solution
                </div>
                <h3 className="mb-4 text-2xl font-bold text-gray-900">
                  Custom Plan
                </h3>
                <p className="mb-6 text-gray-600">
                  Need something more tailored to your business? Let's create a custom plan that perfectly fits your requirements.
                </p>
                <ul className="mb-8 space-y-3">
                  {[
                    "Customized feature set",
                    "Dedicated account manager",
                    "Priority support",
                    "Custom integration options",
                    "Flexible billing options"
                  ].map((feature, index) => (
                    <li key={index} className="flex gap-2 items-center">
                      <svg className="w-5 h-5 text-[#FF6C6C]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center md:text-left">
                <button
                  // onClick={handleContactSales}
                  className="inline-flex items-center px-6 py-3 text-lg font-semibold text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF9494] rounded-full shadow-lg hover:shadow-xl transform transition-all duration-200 hover:-translate-y-1"
                >
                  Coming Soon
                  {/* <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg> */}
                </button>
                <p className="mt-4 text-sm text-gray-500">
                  Get a response within 24 hours
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="pb-24 mx-auto max-w-3xl"
        >
          <h2 className="mb-12 text-3xl font-bold text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {[
              {
                q: "Can I change my plan later?",
                a: "Yes, you can upgrade or downgrade your plan at any time. Changes will be reflected in your next billing cycle.",
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept only Mobile Money payments for now. We are working on adding other payment methods soon.",
              },
              {
                q: "Is there a long-term contract?",
                a: "No, all our plans are billed monthly and you can cancel at any time.",
              },
            ].map((faq, index) => (
              <div key={index} className="p-6 bg-white rounded-lg shadow-sm">
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  {faq.q}
                </h3>
                <p className="text-gray-600">{faq.a}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Pricing;
